import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router';
import { AppLayout, FullScreenLoader, RequireAuth, RouteError, type RouteHandle } from './components/shell';

/** Lazy route module loader: route files `export default` their page component. */
const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({ Component: (await load()).default });
/** Same, for a route module with several named page exports. */
const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, key: K) => async () => ({ Component: (await load())[key] });
const h = (handle: RouteHandle): RouteHandle => handle;
const hydrate = { hydrateFallbackElement: <FullScreenLoader /> };

/**
 * Route table. Every route carries `handle: RouteHandle` (title, helpKey, optional tourId) which drives the
 * document title, breadcrumbs, the `?` help panel and first-run tours. Areas nest their sub-pages with `<Routes>`
 * (paths end in `/*`); those pages publish their own title/helpKey through `PageHeader` (see `usePageMeta`).
 */
export const routes: RouteObject[] = [
  { path: '/login', lazy: page(() => import('./routes/auth/Login')), handle: h({ title: 'Sign in', helpKey: 'home' }), errorElement: <RouteError />, ...hydrate },
  { path: '/signup', lazy: page(() => import('./routes/auth/Signup')), handle: h({ title: 'Create your free account', helpKey: 'onboarding' }), errorElement: <RouteError />, ...hydrate },
  { path: '/register', element: <Navigate to="/signup" replace /> },
  { path: '/start', lazy: page(() => import('./routes/auth/Start')), handle: h({ title: 'Try it with sample data', helpKey: 'onboarding' }), errorElement: <RouteError />, ...hydrate },
  { path: '/forgot-password', lazy: named(() => import('./routes/auth/Recovery'), 'ForgotPasswordPage'), handle: h({ title: 'Reset your password', helpKey: 'settings.security' }), errorElement: <RouteError />, ...hydrate },
  { path: '/reset-password', lazy: named(() => import('./routes/auth/Recovery'), 'ResetPasswordPage'), handle: h({ title: 'Choose a new password', helpKey: 'settings.security' }), errorElement: <RouteError />, ...hydrate },
  { path: '/verify-email', lazy: named(() => import('./routes/auth/Recovery'), 'VerifyEmailPage'), handle: h({ title: 'Confirm your email', helpKey: 'settings.security' }), errorElement: <RouteError />, ...hydrate },
  {
    path: '/onboarding',
    element: (
      <RequireAuth requireEntity={false}>
        <Outlet />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    ...hydrate,
    children: [{ path: ':step?', lazy: page(() => import('./routes/onboarding')), handle: h({ title: 'Get started', helpKey: 'onboarding' }) }],
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    ...hydrate,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { index: true, lazy: page(() => import('./routes/home')), handle: h({ title: 'Home', helpKey: 'home', tourId: 'home' }) },
          { path: 'accounts/*', lazy: page(() => import('./routes/accounts')), handle: h({ title: 'Accounts', helpKey: 'accounts', tourId: 'accounts' }) },
          { path: 'transactions/*', lazy: page(() => import('./routes/transactions')), handle: h({ title: 'Transactions', helpKey: 'transactions', tourId: 'transactions' }) },
          { path: 'budget/*', lazy: page(() => import('./routes/budget')), handle: h({ title: 'Budget', helpKey: 'budget', tourId: 'budget' }) },
          { path: 'goals/*', lazy: page(() => import('./routes/goals')), handle: h({ title: 'Goals', helpKey: 'goals', tourId: 'goals' }) },
          { path: 'invest/*', lazy: page(() => import('./routes/invest')), handle: h({ title: 'Invest', helpKey: 'invest.holdings', tourId: 'invest' }) },
          { path: 'plan/*', lazy: page(() => import('./routes/plan')), handle: h({ title: 'Plan', helpKey: 'plan.forecast', tourId: 'plan' }) },
          { path: 'reports/*', lazy: page(() => import('./routes/reports')), handle: h({ title: 'Reports', helpKey: 'reports', tourId: 'reports' }) },
          { path: 'business/*', lazy: page(() => import('./routes/business')), handle: h({ title: 'Business', helpKey: 'business.invoices', tourId: 'business' }) },
          {
            path: 'settings',
            lazy: page(() => import('./routes/settings')),
            handle: h({ title: 'Settings', helpKey: 'settings.profile' }),
            children: [
              { index: true, element: <Navigate to="profile" replace /> },
              { path: 'profile', lazy: page(() => import('./routes/settings/profile')), handle: h({ title: 'Profile & preferences', helpKey: 'settings.profile' }) },
              { path: 'security', lazy: page(() => import('./routes/settings/security')), handle: h({ title: 'Security', helpKey: 'settings.security' }) },
              { path: 'entity', lazy: page(() => import('./routes/settings/entity')), handle: h({ title: 'Entity', helpKey: 'settings.entity' }) },
              { path: 'members', lazy: page(() => import('./routes/settings/members')), handle: h({ title: 'Members', helpKey: 'settings.members' }) },
              { path: 'data', lazy: page(() => import('./routes/settings/data')), handle: h({ title: 'Data', helpKey: 'settings.data' }) },
              { path: 'notifications', lazy: page(() => import('./routes/settings/notifications')), handle: h({ title: 'Notifications', helpKey: 'settings.notifications' }) },
              { path: 'billing', lazy: page(() => import('./routes/settings/billing')), handle: h({ title: 'Plan & billing', helpKey: 'settings.profile' }) },
              { path: 'achievements', lazy: page(() => import('./routes/settings/achievements')), handle: h({ title: 'Achievements', helpKey: 'settings.profile' }) },
            ],
          },
          { path: 'journey', lazy: page(() => import('./routes/journey')), handle: h({ title: 'Journey & badges', helpKey: 'journey' }) },
          { path: 'review/weekly', lazy: page(() => import('./routes/review/weekly')), handle: h({ title: 'Weekly review', helpKey: 'review.weekly' }) },
          { path: 'checkin', lazy: page(() => import('./routes/checkin')), handle: h({ title: 'Daily check-in', helpKey: 'checkin.daily' }) },
          { path: 'admin/funnel', lazy: page(() => import('./routes/admin/funnel')), handle: h({ title: 'Signup funnel', helpKey: 'reports' }) },
          { path: 'help/shortcuts', lazy: page(() => import('./routes/help/shortcuts')), handle: h({ title: 'Keyboard shortcuts', helpKey: 'help.shortcuts' }) },
          { path: '*', lazy: page(() => import('./routes/not-found')), handle: h({ title: 'Page not found', helpKey: 'home' }) },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
